import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { apiClient, formatSAR } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../lib/auth";
import {
  Star, MapPin, Phone, Building2, ShieldCheck, Sparkles, Filter, Search,
  ArrowRight, BadgeCheck,
} from "lucide-react";

const CATEGORY_META = {
  showroom:    { ar: "نقل الملكية", en: "Ownership Transfer", icon: "🏢", color: "#c9a55c" },
  insurance:   { ar: "شركات التأمين",     en: "Insurance",                 icon: "🛡️", color: "#8a6f2e" },
  inspection:  { ar: "مراكز الفحص",       en: "Inspection Centers",        icon: "🔍", color: "#5a4615" },
  transport:   { ar: "النقل والشحن",       en: "Transport & Delivery",      icon: "🚛", color: "#3a3a3a" },
  maintenance: { ar: "الصيانة والتجهيز",   en: "Maintenance & Setup",       icon: "🔧", color: "#7a7a7a" },
};

function PartnerCard({ p, lang }) {
  const meta = CATEGORY_META[p.category] || CATEGORY_META.showroom;
  return (
    <Link to={`/services/partner/${p.id}`} className="surface rounded-2xl overflow-hidden hover:shadow-lg transition-all block" data-testid={`partner-${p.id}`}>
      <div className="h-32 relative" style={{ background: `linear-gradient(135deg, ${meta.color}30, ${meta.color}80)` }}>
        {p.cover_image && <img src={p.cover_image} alt="" className="w-full h-full object-cover" />}
        {p.logo && <img src={p.logo} alt="" className="absolute bottom-2 start-3 w-14 h-14 rounded-xl bg-white object-contain border-2 border-white shadow" />}
        <div className="absolute top-2 end-2 flex gap-1 flex-wrap justify-end">
          {p.featured && <span className="text-[10px] px-2 py-1 rounded-full bg-[#c9a55c] text-[#0a0a0a] font-bold">⭐ مميز</span>}
          {p.verified && <span className="text-[10px] px-2 py-1 rounded-full bg-green-600 text-white font-semibold inline-flex items-center gap-1"><BadgeCheck className="w-3 h-3" /> موثّق</span>}
          {p.is_new && !p.verified && <span className="text-[10px] px-2 py-1 rounded-full bg-blue-600 text-white font-semibold">جديد</span>}
        </div>
      </div>
      <div className="p-4">
        <h3 className="font-display text-lg mb-1">{lang === "ar" ? p.name_ar : (p.name_en || p.name_ar)}</h3>
        <div className="text-xs text-[#6b6b6b] space-y-1">
          <div className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {p.city}</div>
          {p.phone && <div className="inline-flex items-center gap-1 ms-2"><Phone className="w-3 h-3" /> {p.phone}</div>}
        </div>
        <div className="flex justify-between items-center mt-3 pt-3 border-t border-[#0a0a0a]/[0.06]">
          <div className="inline-flex items-center gap-1 text-sm">
            <Star className="w-4 h-4 text-[#c9a55c] fill-current" />
            <span className="font-semibold">{p.rating_avg?.toFixed(1) || "—"}</span>
            <span className="text-xs text-[#8a8a8a]">({p.rating_count || 0})</span>
          </div>
          {p.base_fee_sar > 0 && <div className="text-sm font-bold text-[#8a6f2e]">{formatSAR(p.base_fee_sar)} ريال</div>}
        </div>
      </div>
    </Link>
  );
}

export default function ServicesDirectoryPage() {
  const { category } = useParams();
  const { lang } = useI18n();
  const navigate = useNavigate();
  const [data, setData] = useState({ items: [], total: 0 });
  const [filters, setFilters] = useState({ city: "", q: "", sort: "featured" });

  useEffect(() => {
    const params = { category, sort: filters.sort };
    if (filters.city) params.city = filters.city;
    if (filters.q) params.q = filters.q;
    apiClient.get("/partners", { params }).then(({ data }) => setData(data));
  }, [category, filters]);

  const meta = CATEGORY_META[category];
  if (!meta) {
    return (
      <div className="max-w-2xl mx-auto p-12 text-center">
        <h1 className="font-display text-2xl">{lang === "ar" ? "الفئة غير معروفة" : "Unknown category"}</h1>
        <Link to="/services" className="btn-primary mt-4 inline-flex">{lang === "ar" ? "كل الخدمات" : "All services"}</Link>
      </div>
    );
  }

  return (
    <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12 py-10" data-testid={`services-${category}`}>
      <div className="flex items-end justify-between gap-4 flex-wrap mb-8">
        <div>
          <div className="overline overline-gold mb-2">{lang === "ar" ? "إكمال الصفقة" : "Complete the deal"}</div>
          <h1 className="font-display text-4xl sm:text-5xl tracking-tight font-medium">
            <span className="me-2">{meta.icon}</span>
            {lang === "ar" ? meta.ar : meta.en}
          </h1>
          <p className="text-[#525252] mt-2">{lang === "ar" ? `اختر من ${data.total} مزوّد خدمة معتمد` : `Choose from ${data.total} verified providers`}</p>
        </div>
        <button onClick={() => navigate("/join?intent=partner")} className="btn-ghost text-sm">
          {lang === "ar" ? "هل تقدّم هذه الخدمة؟ سجّل كشريك" : "Provide this service? Become a partner"} <ArrowRight className="w-4 h-4 rtl:rotate-180" />
        </button>
      </div>

      <div className="surface rounded-xl p-4 mb-6 grid sm:grid-cols-3 gap-3">
        <div className="relative">
          <Search className="absolute top-1/2 -translate-y-1/2 start-3 w-4 h-4 text-[#8a8a8a]" />
          <input className="input-dark ps-9 w-full" placeholder={lang === "ar" ? "ابحث بالاسم..." : "Search by name..."} value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} data-testid="partners-search" />
        </div>
        <input className="input-dark" placeholder={lang === "ar" ? "المدينة (الرياض، جدة...)" : "City"} value={filters.city} onChange={(e) => setFilters({ ...filters, city: e.target.value })} data-testid="partners-city" />
        <select className="input-dark" value={filters.sort} onChange={(e) => setFilters({ ...filters, sort: e.target.value })} data-testid="partners-sort">
          <option value="featured">{lang === "ar" ? "الأبرز" : "Featured"}</option>
          <option value="rating">{lang === "ar" ? "الأعلى تقييماً" : "Highest rated"}</option>
          <option value="newest">{lang === "ar" ? "الأحدث" : "Newest"}</option>
          <option value="price">{lang === "ar" ? "الأقل سعراً" : "Cheapest"}</option>
        </select>
      </div>

      {data.items.length === 0 ? (
        <div className="surface rounded-xl p-16 text-center">
          <Building2 className="w-12 h-12 mx-auto text-[#c9a55c] opacity-60 mb-4" />
          <h3 className="font-display text-2xl mb-2">{lang === "ar" ? "لا يوجد شركاء بعد" : "No partners yet"}</h3>
          <p className="text-[#6b6b6b] mb-6">{lang === "ar" ? "كن أول من يقدّم هذه الخدمة على كارتال" : "Be the first to offer this service on CARTAL"}</p>
          <button onClick={() => navigate("/join?intent=partner")} className="btn-primary mx-auto"><Sparkles className="w-4 h-4" /> {lang === "ar" ? "سجّل كشريك" : "Register as partner"}</button>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {data.items.map(p => <PartnerCard key={p.id} p={p} lang={lang} />)}
        </div>
      )}
    </div>
  );
}
